/**
 * Harvest and the seed economy.
 *
 * Pure arithmetic over plain data, like `progress.ts` — no Three.js, no DOM,
 * no storage. The tuning lives here so it can be read in one sitting.
 *
 * ## The thing this exists to solve
 *
 * Before this, a full plot was a dead end. Twelve beds filled and the loop
 * stopped: `isFull` fired and there was nothing left to do but look at it.
 * Harvest is what lets the garden keep going — a mature specimen can be
 * lifted, freeing its bed and banking Seedlings.
 *
 * ## What it deliberately is not
 *
 * A gate. Every one of the six species stays freely sowable by hand, forever,
 * for nothing — exactly as it was before this file existed. Seedlings buy a
 * *seed pack*, which is a convenience and a lottery: one tap fills up to three
 * beds with a draw weighted by each species' rarity.
 *
 * So the pack is priced to be generous rather than grindy. Three common
 * harvests pay for a pack of three, which means the loop sustains itself and
 * then some. Nothing here should ever make a player feel they must harvest,
 * must return, or must spend — see `docs/GAMEPLAY.md` on which half of the
 * idle-game playbook this project takes and which half it refuses.
 *
 * And nothing here touches growth *time*. Pay-to-skip is the line this does
 * not cross: a pack's seeds go into the ground at zero, like every other seed.
 */

import { PACK_DRAW, SPECIES, type Rarity, type SpeciesId } from '../content/species';
import type { Mutation } from '../content/weather';

/* ------------------------------------------------------------------ *
 * Harvest
 * ------------------------------------------------------------------ */

/**
 * Seedlings banked for lifting one mature specimen.
 *
 * Scaled by rarity — so the species that drop least often are the ones
 * worth most to harvest, the same truth the score already teaches.
 */
export const HARVEST_YIELD: Record<Rarity, number> = {
  common: 3,
  uncommon: 5,
  rare: 9,
  legendary: 25,
};

/**
 * What lifting this specimen pays.
 *
 * A weather mark multiplies, using the same `bonus` the register score uses.
 * That is deliberate: a sandstorm costs a plant time and then pays 40% more
 * for it, and it would be incoherent for that to hold on the scoreboard but
 * not in the hand.
 */
export function harvestYield(rarity: Rarity, mutation: Mutation | null): number {
  return Math.round(HARVEST_YIELD[rarity] * (mutation ? mutation.bonus : 1));
}

/* ------------------------------------------------------------------ *
 * Seed packs
 * ------------------------------------------------------------------ */

export const PACK_COST = 8;
export const PACK_SIZE = 3;

/*
 * Which species a pack draws from, and the odds of the special one, are
 * content: `PACK_DRAW` in `src/content/species.ts` explains both.
 */
const FIELD = PACK_DRAW.field;

/**
 * Draw one seed. `roll` returns [0, 1) and is injected so a caller can make
 * the draw reproducible; the game passes `Math.random`.
 */
export function drawSeed(roll: () => number): SpeciesId {
  if (roll() < PACK_DRAW.special.chance) return PACK_DRAW.special.id;

  let ticket = roll() * 100;
  for (const id of FIELD) {
    // Non-null: every field species has a published share. The conservatory
    // ornamental is the only null, and it was drawn out above.
    ticket -= SPECIES[id].drawShare ?? 0;
    if (ticket <= 0) return id;
  }
  // Floating-point crumbs at the very top of the range.
  return FIELD[FIELD.length - 1];
}

/** A whole pack. `limit` caps it to the beds actually free. */
export function drawPack(roll: () => number, limit = PACK_SIZE): SpeciesId[] {
  const n = Math.max(0, Math.min(PACK_SIZE, limit));
  return Array.from({ length: n }, () => drawSeed(roll));
}
